import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { confirmationFileFilters, costsFilters, instructionFileFilters, walletListFilters } from './wallet.filters';
import {
  confirmationLinesPath,
  CREATE_SHEET,
  instructionLinesPath,
  WALLET_LIST_PATH,
  WALLET_TAB_IDS,
  WALLET_TABS,
  walletPath,
  type WalletDetailSheet,
} from './wallet.search';

// Manifestos de Carteiras (U23/U42). Leve: o fio entra por import dinâmico no `run`. As ações com
// efeito têm `preview` (U24) com o texto do legado e o verbo (U20-6). As telas trocam o `run` dos
// downloads pelo da tela (andamento, cancelar e toast), mantendo o mesmo id.

const api = () => import('./wallet.api');
const save = async (file: Promise<{ readonly blob: Blob; readonly fileName: string }>) => {
  const [{ downloadBlob }, done] = await Promise.all([import('@/shared/http/download'), file]);
  downloadBlob(done.blob, done.fileName);
  return done.fileName;
};

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
const OPEN_WALLET_ONLY = 'This action only runs on the wallet detail screen, on the wallet that is open there.';
/** As ações presas à carteira aberta: fora do detalhe não há carteira, e a tela troca `run` e `preview`. */
const needsOpenWallet = () => {
  throw new Error(OPEN_WALLET_ONLY);
};

const ref = z.object({ id: z.string().min(1).max(64), name: z.string().max(200).optional() });
const fileRef = z.object({ id: z.string().min(1).max(64), name: z.string().max(260).optional(), walletCode: z.union([z.number(), z.string()]).optional() });
const walletCode = z.object({ walletCode: z.number().int() });
const sheet = (name: WalletDetailSheet) => ({ nav }: { nav: { search: (patch: Record<string, unknown>) => unknown } }) => nav.search({ sheet: name });

// ─── lista ─────────────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`WalletsListHeader.tsx:35-39`). A tela liga o `run` ao refetch. */
export const walletRefresh = action({
  id: 'wallet.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca a lista de carteiras de novo. Nada é alterado.`,
  run: () => undefined,
});

/** "Adicionar carteira" (`WalletsListHeader.tsx:40-46`): `BillingWallet:Create` e tenant raiz (a tela confere a raiz). */
export const walletCreate = action({
  id: 'wallet.create',
  kind: 'draft',
  label: msg`Adicionar carteira`,
  description: msg`Abre o painel de nova carteira em branco: conta bancária, produto de cobrança, parâmetros de custos de cobrança e nome da carteira. Só aparece para o correspondente raiz.`,
  permission: can('BillingWallet', 'Create'),
  run: ({ nav }) => nav.search({ sheet: CREATE_SHEET }),
});

/** "Visualizar" e clique na linha (`WalletsList.tsx:121, 163`). */
export const walletOpen = action({
  id: 'wallet.open',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Visualizar`,
  description: msg`Abre uma carteira pelo id: cabeçalho, tarifas, arquivos CNAB de remessa e de retorno e tarifas lançadas.`,
  input: ref,
  run: ({ nav, input }) => nav.to(walletPath(input.id)),
});

/** "Desativar" (`WalletsList.tsx:127-133`, modal `WalletsListContainer.tsx:111-125`): `DELETE /Wallet/{id}`. */
export const walletRemove = action({
  id: 'wallet.remove',
  kind: 'destructive',
  page: true,
  row: true,
  label: msg`Desativar`,
  description: msg`Desativa uma carteira pelo id. Pede a confirmação da pessoa, com o titular e o código do convênio da carteira.`,
  permission: can('BillingAccount', 'Delete'),
  input: ref,
  preview: (input) => {
    const name = input.name ?? input.id;
    return { title: i18n._(msg`Desativar carteira ${name}`), detail: i18n._(msg`Tem certeza que deseja desativar essa carteira?`), tone: 'destructive' };
  },
  run: async ({ input }) => (await api()).deleteWallet(input.id),
});

export const walletFilter = filterAction('wallet.filter', walletListFilters, {
  description: msg`Filtra a lista de carteiras: searchString (todos os campos), walletsCode (um ou mais códigos de convênio), name (titular), alias (nome da carteira) e bankAccount (conta). Filtro omitido é limpo.`,
});

export const walletListPage = listPageAction('wallet.list.page');

export const walletListScreen = defineScreen({
  id: 'wallet.list',
  path: WALLET_LIST_PATH,
  title: msg`Carteiras`,
  description: msg`Carteiras de cobrança (convênios) do correspondente: lista paginada com filtros, adicionar carteira (correspondente raiz) e desativar.`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'wallet', section: 'wallet-management' },
  actions: [walletRefresh, walletCreate, walletOpen, walletRemove, walletFilter, walletListPage],
});

// ─── detalhe ───────────────────────────────────────────────────────────────────────────────────────

export const walletDetailRefresh = action({
  id: 'wallet.detail.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca a carteira aberta de novo. Nada é alterado.`,
  run: () => undefined,
});

const walletTabList = WALLET_TAB_IDS.join(', ');

export const walletTab = action<{ tab: (typeof WALLET_TAB_IDS)[number] }>({
  id: 'wallet.detail.tab',
  kind: 'navigate',
  label: msg`Trocar de aba`,
  description: msg`Troca a aba da carteira aberta. Abas: ${walletTabList}.`,
  input: z.object({ tab: z.enum(WALLET_TAB_IDS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === WALLET_TAB_IDS[0] ? undefined : input.tab, page: undefined, size: undefined, sheet: undefined }),
});

export const walletDetailPage = listPageAction('wallet.detail.page');

/** "Atualizar custos" (`WalletFormHeader.tsx:72-78`): raiz e a ação `UpdateBillingCosts`. */
export const walletUpdateCosts = action({
  id: 'wallet.updateCosts',
  kind: 'draft',
  label: msg`Atualizar custos`,
  description: msg`Abre o painel de custos da carteira: os onze custos de boleto e os custos de Pix (valores em centavos; percentual de processamento do Pix). Só para o correspondente raiz.`,
  permission: can('BillingAccount', 'UpdateBillingCosts', { strict: true }),
  run: sheet('custos'),
});

/** "Atualizar carteira" (`WalletFormHeader.tsx:86-92`): raiz. */
export const walletUpdate = action({
  id: 'wallet.update',
  kind: 'draft',
  label: msg`Atualizar carteira`,
  description: msg`Abre o painel da carteira: tipo de cobrança (mensal ou diária), nome da carteira e protesto automático. Só para o correspondente raiz.`,
  run: sheet('carteira'),
});

/** "Atualizar e-mails" (`WalletFormHeader.tsx:100-106`). */
export const walletUpdateEmails = action({
  id: 'wallet.updateEmails',
  kind: 'draft',
  label: msg`Atualizar e-mails`,
  description: msg`Abre o painel dos e-mails que recebem os avisos da carteira aberta.`,
  run: sheet('emails'),
});

/**
 * "Cobrar tarifa pendente" (`WalletFormContainer.tsx:190-204`): debita as tarifas pendentes da conta tarifa.
 * Presa à carteira aberta, como o legado (`manualDebtFromWalletMutate(id!)`, o id da rota): a entrada é
 * vazia e estrita, e a tela de detalhe troca `run` e `preview` pelos da carteira da rota (B1 da !11134).
 */
export const walletManualDebit = action({
  id: 'wallet.manualDebit',
  kind: 'write',
  label: msg`Cobrar tarifa pendente`,
  description: msg`Cobra agora as tarifas pendentes da carteira aberta na tela, na conta tarifa dela. Movimenta dinheiro. Não recebe entrada: a carteira é sempre a aberta e não pode ser escolhida; para cobrar outra, abra-a antes. Só para o correspondente raiz. Pede a confirmação da pessoa.`,
  input: z.strictObject({}),
  preview: () => ({ title: i18n._(msg`Cobrar tarifa pendente`), detail: i18n._(msg`Tem certeza que deseja cobrar as tarifas pendentes desta carteira?`) }),
  run: needsOpenWallet,
});

/** "Exportar posição carteira" (`WalletFormHeader.tsx:107-113`). No detalhe, o agente exporta a carteira aberta. */
export const walletExportPosition = action({
  id: 'wallet.exportPosition',
  kind: 'read',
  page: true,
  label: msg`Exportar posição carteira`,
  description: msg`Baixa o relatório de posição da carteira de um código de convênio (walletCode).`,
  input: walletCode,
  run: async ({ input }) => save((await api()).exportWalletPosition(input.walletCode)),
});

/** Quem recebe a carteira: operador (`userId`) ou grupo (`groupName`), opcionalmente de outro correspondente. */
export const walletAssignInput = z.strictObject({ userId: z.string().min(1).max(64).optional(), groupName: z.string().min(1).max(200).optional(), tenant: z.string().min(1).max(64).optional() });

/**
 * "Atribuir" (`WalletFormHeader.tsx:114-120`, `AssignDrawer recordType='Wallet'`). Presa à carteira aberta:
 * a tela troca `run` e `preview` pelos da carteira da rota, com o nome do operador, do grupo e do correspondente.
 */
export const walletAssign = action({
  id: 'wallet.assign',
  kind: 'write',
  label: msg`Atribuir`,
  description: msg`Atribui a carteira aberta na tela a um operador (userId) ou a um grupo (groupName), se preciso de outro correspondente (tenant). A carteira é sempre a aberta e não pode ser escolhida. Pede a confirmação da pessoa.`,
  permission: can('BillingAccount', 'Update'),
  input: walletAssignInput,
  preview: () => ({ title: i18n._(msg`Atribuir`), detail: i18n._(msg`Selecione um correspondente, operador ou grupo`) }),
  run: needsOpenWallet,
});

// Aba Arquivos remessa (`FileInstructionHeader.tsx`, `FileInstructionList.tsx`).

export const walletImportCnab = action({
  id: 'wallet.importCnab',
  kind: 'draft',
  label: msg`Importar arquivo CNAB`,
  description: msg`Abre o painel de importar arquivo CNAB da aba Arquivos remessa: primeiro valida, depois importa.`,
  run: ({ nav }) => nav.search({ tab: 'arquivos-remessa', sheet: 'importar-cnab' }),
});

export const walletGenerateCnab = action({
  id: 'wallet.generateCnab',
  kind: 'draft',
  label: msg`Gerar CNAB 400`,
  description: msg`Abre o painel de gerar CNAB 400 da aba Arquivos remessa, com período opcional.`,
  run: ({ nav }) => nav.search({ tab: 'arquivos-remessa', sheet: 'gerar-cnab' }),
});

export const walletInstructionDownload = action({
  id: 'wallet.instructionFile.download',
  kind: 'read',
  page: true,
  row: true,
  label: msg`Download`,
  description: msg`Baixa um arquivo CNAB de remessa pelo id.`,
  input: fileRef,
  run: async ({ input }) => save((await api()).downloadFileInstruction(input.id, input.name ?? `remessa-${input.id}`)),
});

export const walletInstructionLines = action({
  id: 'wallet.instructionFile.lines',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Ver transações`,
  description: msg`Abre as transações de um arquivo CNAB de remessa.`,
  input: fileRef,
  run: ({ nav, input }) => nav.to(instructionLinesPath(input.id)),
});

export const walletInstructionFilter = filterAction('wallet.instructionFile.filter', instructionFileFilters, {
  description: msg`Filtra a aba Arquivos remessa pela data do upload (initialDate e finalDate, datas civis AAAA-MM-DD). Filtro omitido é limpo.`,
});

// Aba Arquivos retorno (`FileConfirmationList.tsx:73-110`).

export const walletConfirmationDownload = action({
  id: 'wallet.confirmationFile.download',
  kind: 'read',
  page: true,
  row: true,
  label: msg`Download Retorno (RET)`,
  description: msg`Baixa um arquivo CNAB de retorno (RET) pelo id.`,
  input: fileRef,
  run: async ({ input }) => save((await api()).downloadFileConfirmation(input.id, input.name ?? `retorno-${input.id}`)),
});

const francesinhaInput = fileRef.extend({ walletCode: z.union([z.number(), z.string()]) });

export const walletFrancesinhaPdf = action({
  id: 'wallet.confirmationFile.francesinha',
  kind: 'read',
  page: true,
  row: true,
  label: msg`Download Francesinha (PDF)`,
  description: msg`Baixa o relatório francesinha em PDF de um arquivo de retorno.`,
  input: francesinhaInput,
  run: async ({ input }) => save((await api()).downloadFrancesinhaPdf({ fileConfirmationIds: [input.id], walletsCode: [input.walletCode] }, `francesinha-${input.id}.pdf`)),
});

export const walletFrancesinhaUnified = action({
  id: 'wallet.confirmationFile.francesinhaUnified',
  kind: 'read',
  page: true,
  row: true,
  label: msg`Download Francesinha Unificada (PDF)`,
  description: msg`Baixa a francesinha unificada em PDF de um arquivo de retorno.`,
  input: francesinhaInput,
  run: async ({ input }) => save((await api()).downloadFrancesinhaPdf({ fileConfirmationIds: [input.id], walletsCode: [input.walletCode], WithUnifiedBankSlip: true }, `francesinha-unificada-${input.id}.pdf`)),
});

export const walletFrancesinhaCsv = action({
  id: 'wallet.confirmationFile.francesinhaCsv',
  kind: 'read',
  page: true,
  row: true,
  label: msg`Download Francesinha Unificada (CSV)`,
  description: msg`Baixa a francesinha unificada em CSV de um arquivo de retorno.`,
  input: francesinhaInput,
  run: async ({ input }) => save((await api()).downloadFrancesinhaCsv({ fileConfirmationIds: [input.id], walletsCode: [input.walletCode], WithUnifiedBankSlip: true }, `francesinha-unificada-${input.id}.csv`)),
});

export const walletConfirmationLines = action({
  id: 'wallet.confirmationFile.lines',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Ver transações`,
  description: msg`Abre as transações de um arquivo CNAB de retorno.`,
  input: fileRef,
  run: ({ nav, input }) => nav.to(confirmationLinesPath(input.id)),
});

export const walletConfirmationFilter = filterAction('wallet.confirmationFile.filter', confirmationFileFilters, {
  description: msg`Filtra a aba Arquivos retorno pela data do upload (initialDate e finalDate, datas civis AAAA-MM-DD). Filtro omitido é limpo.`,
});

// Aba Tarifas do convênio (`CostsListHeader.tsx`).

export const walletCostsExport = action({
  id: 'wallet.costs.export',
  kind: 'read',
  label: msg`Exportar tarifas`,
  description: msg`Baixa as tarifas lançadas da carteira aberta, com os filtros da tela.`,
  run: () => undefined,
});

export const walletCostsFilter = filterAction('wallet.costs.filter', costsFilters, {
  description: msg`Filtra a aba Tarifas do convênio: período de cobrança, período de lançamento, tipo de instrução e se a tarifa já foi cobrada. Filtro omitido é limpo.`,
});

export const walletDetailScreen = defineScreen({
  id: 'wallet.detail',
  path: `${WALLET_LIST_PATH}/$walletId`,
  title: msg`Carteira`,
  description: msg`Uma carteira de cobrança: cabeçalho com a conta, as tarifas e as cobranças pendentes; abas com a tabela de custos, os arquivos CNAB de remessa (importar, gerar, baixar), os de retorno (RET e francesinha) e as tarifas lançadas.`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'wallet', parent: 'wallet.list', record: msg`Carteira`, tabs: WALLET_TABS },
  actions: [
    walletDetailRefresh,
    walletTab,
    walletDetailPage,
    walletUpdateCosts,
    walletUpdate,
    walletManualDebit,
    walletUpdateEmails,
    walletExportPosition,
    walletAssign,
    walletImportCnab,
    walletGenerateCnab,
    walletInstructionDownload,
    walletInstructionLines,
    walletInstructionFilter,
    walletConfirmationDownload,
    walletFrancesinhaPdf,
    walletFrancesinhaUnified,
    walletFrancesinhaCsv,
    walletConfirmationLines,
    walletConfirmationFilter,
    walletCostsExport,
    walletCostsFilter,
  ],
});

// ─── transações do arquivo ─────────────────────────────────────────────────────────────────────────

export const walletLinesRefresh = action({
  id: 'wallet.lines.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo as transações do arquivo CNAB aberto. Nada é alterado.`,
  run: () => undefined,
});

export const walletLinesPage = listPageAction('wallet.lines.page');

const lineRef = z.object({ id: z.string().min(1).max(64) });

export const walletLineDetails = action({
  id: 'wallet.lines.details',
  kind: 'read',
  page: true,
  row: true,
  label: msg`Ver detalhes`,
  description: msg`Abre o painel de detalhes de uma transação: status, código de barras, pagador e valores.`,
  input: lineRef,
  run: ({ nav, input }) => nav.search({ linha: input.id }),
});

export const walletLineReceipt = action({
  id: 'wallet.lines.receipt',
  kind: 'read',
  page: true,
  row: true,
  label: msg`Download Recibo`,
  description: msg`Baixa o recibo de uma transação do arquivo de retorno.`,
  input: lineRef,
  run: async ({ input }) => save((await api()).downloadTransactionReceipt(input.id)),
});

export const walletLineBankSlip = action({
  id: 'wallet.lines.bankSlip',
  kind: 'read',
  page: true,
  label: msg`Baixar boleto`,
  description: msg`Baixa o PDF atual do boleto de uma transação do arquivo de remessa, pelo código de barras e o código do convênio.`,
  input: z.object({ barCode: z.string().min(1).max(100), walletCode: z.string().max(20) }),
  run: async ({ input }) => save((await api()).downloadBankSlipPdf(input.barCode, input.walletCode)),
});

export const walletInstructionLinesScreen = defineScreen({
  id: 'wallet.lines.instruction',
  path: `${WALLET_LIST_PATH}/arquivos/$fileId/remessa`,
  title: msg`Transações do arquivo remessa`,
  description: msg`Transações de um arquivo CNAB de remessa: instrução, número do documento, pagador, data e status de registro, com o painel de detalhes e o download do boleto.`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'file-text', parent: 'wallet.list', record: msg`Remessa` },
  actions: [walletLinesRefresh, walletLinesPage, walletLineDetails, walletLineBankSlip],
});

export const walletConfirmationLinesScreen = defineScreen({
  id: 'wallet.lines.confirmation',
  path: `${WALLET_LIST_PATH}/arquivos/$fileId/retorno`,
  title: msg`Transações do arquivo retorno`,
  description: msg`Transações de um arquivo CNAB de retorno: instrução, número do documento, pagador e data, com o painel de detalhes e o download do recibo.`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'file-text', parent: 'wallet.list', record: msg`Retorno` },
  actions: [walletLinesRefresh, walletLinesPage, walletLineDetails, walletLineReceipt],
});
